import { useCallback, useEffect, useMemo, useState } from "react";

const LEGACY_CATEGORY_OPTIONS = [
  { key: "file_name", label: "\uD30C\uC77C\uBA85" },
  { key: "facility_type", label: "\uC124\uBE44 \uAD6C\uBD84" },
  { key: "location", label: "\uC704\uCE58" },
  { key: "facility_name", label: "\uC124\uBE44\uBA85" },
];

function getLegacyCategoryKey(label) {
  const found = LEGACY_CATEGORY_OPTIONS.find((item) => item.label === label);
  return found?.key ?? "";
}

function getValidCategoryKey(categoryKey, options, fallbackKey) {
  if (options.some((item) => item.key === categoryKey)) {
    return categoryKey;
  }
  if (options.some((item) => item.key === fallbackKey)) {
    return fallbackKey;
  }
  return options[0]?.key ?? "file_name";
}

export default function FileSearchBox({
  rawData = [],
  setSortedData,
  isCategorieSelectDefault = "\uD30C\uC77C\uBA85",
  categoryOptions,
  defaultCategoryKey,
  placeholderSuffix = "\uAC80\uC0C9",
  searchCategory,
  searchKeyword = "",
  onSearchChange,
  debounceMs = 350,
  layout = "default",
  categoryLabel = "검색 기준",
  keywordLabel = "검색어",
}) {
  const options = useMemo(() => {
    if (Array.isArray(categoryOptions) && categoryOptions.length > 0) {
      return categoryOptions;
    }
    return LEGACY_CATEGORY_OPTIONS;
  }, [categoryOptions]);

  const fallbackCategoryKey =
    defaultCategoryKey ||
    getLegacyCategoryKey(isCategorieSelectDefault) ||
    options[0]?.key ||
    "file_name";

  const isServerSearch = typeof onSearchChange === "function";

  const [selectedCategoryKey, setSelectedCategoryKey] = useState(() =>
    getValidCategoryKey(searchCategory, options, fallbackCategoryKey),
  );
  const [searchInput, setSearchInput] = useState(searchKeyword ?? "");

  const selectedLabel = useMemo(() => {
    return (
      options.find((item) => item.key === selectedCategoryKey)?.label ??
      options[0]?.label ??
      ""
    );
  }, [options, selectedCategoryKey]);

  useEffect(() => {
    setSelectedCategoryKey((currentCategoryKey) =>
      getValidCategoryKey(
        isServerSearch ? searchCategory : currentCategoryKey,
        options,
        fallbackCategoryKey,
      ),
    );
  }, [fallbackCategoryKey, isServerSearch, options, searchCategory]);

  useEffect(() => {
    if (!isServerSearch) return;
    setSearchInput(searchKeyword ?? "");
  }, [isServerSearch, searchKeyword]);

  const applyLocalFilter = useCallback(
    (inputValue, categoryKey) => {
      if (!categoryKey || typeof setSortedData !== "function") return;

      const normalizedInput = String(inputValue ?? "").toLowerCase();
      const filtered = rawData.filter((item) =>
        String(item?.[categoryKey] ?? "")
          .toLowerCase()
          .includes(normalizedInput),
      );
      setSortedData(filtered);
    },
    [rawData, setSortedData],
  );

  useEffect(() => {
    if (isServerSearch) return undefined;

    if (!searchInput) {
      setSortedData?.(rawData);
      return undefined;
    }

    applyLocalFilter(searchInput, selectedCategoryKey);
    return undefined;
  }, [
    applyLocalFilter,
    isServerSearch,
    rawData,
    searchInput,
    selectedCategoryKey,
    setSortedData,
  ]);

  useEffect(() => {
    if (!isServerSearch) return undefined;

    const timerId = window.setTimeout(() => {
      onSearchChange({
        categoryKey: selectedCategoryKey,
        keyword: searchInput,
      });
    }, debounceMs);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [
    debounceMs,
    isServerSearch,
    onSearchChange,
    searchInput,
    selectedCategoryKey,
  ]);

  const handleChangeInput = (event) => {
    const inputValue = event.target.value;
    setSearchInput(inputValue);

    if (!isServerSearch) {
      applyLocalFilter(inputValue, selectedCategoryKey);
    }
  };

  const handleSelectCategory = (categoryKey) => {
    const nextCategoryKey = getValidCategoryKey(
      categoryKey,
      options,
      fallbackCategoryKey,
    );
    setSelectedCategoryKey(nextCategoryKey);

    if (!isServerSearch) {
      applyLocalFilter(searchInput, nextCategoryKey);
    }
  };

  if (layout === "labeled") {
    return (
      <div className="fileListSearchFields">
        <label className="fileListControlField">
          <span>{categoryLabel}</span>
          <select
            value={selectedCategoryKey}
            onChange={(event) => handleSelectCategory(event.target.value)}
          >
            {options.map((item) => (
              <option value={item.key} key={item.key}>{item.label}</option>
            ))}
          </select>
        </label>
        <label className="fileListControlField fileListControlField--keyword">
          <span>{keywordLabel}</span>
          <input
            value={searchInput}
            onChange={handleChangeInput}
            type="search"
            placeholder={`${selectedLabel} ${placeholderSuffix}`}
          />
        </label>
      </div>
    );
  }

  return (
    <div className="searchBox">
      <p>{selectedLabel}</p>
      <ul className="dropdown">
        {options.map((item) => (
          <li onClick={() => handleSelectCategory(item.key)} key={item.key}>
            {item.label}
          </li>
        ))}
      </ul>
      <div>
        <input
          value={searchInput}
          onChange={handleChangeInput}
          type="text"
          placeholder={`${selectedLabel} ${placeholderSuffix}`}
        />
      </div>
    </div>
  );
}
